import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachFileIcon from '@/component/common/icons/AttachFileIcon';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import './AiPreviewUploadFiles.css';

const { UPLOADING, ERROR } = Constant.AI_CHAT_UPLOAD_STATUS;

/**
 * Get the CSS state class of a preview item from its upload status
 * @param {string} uploadStatus - Upload status of the item
 * @returns {string}
 */
const getUploadStatusClassName = (uploadStatus) => {
    if (uploadStatus === UPLOADING) return 'uploading';
    if (uploadStatus === ERROR) return 'upload-error';
    return '';
};

/**
 * Status indicator: loading spinner while uploading, error icon with tooltip on failure
 */
function UploadStatusIndicator({ uploadStatus }) {

    if (uploadStatus === UPLOADING) {
        return (
            <div className="ai-preview-upload-loading-wrapper">
                <CircularProgress size={16} thickness={5} className="ai-preview-upload-loading" />
            </div>
        );
    }

    return null;
}

UploadStatusIndicator.propTypes = {
    uploadStatus: PropTypes.string
};

/**
 * Remove button, shown for every item that is no longer uploading (SUCCESS or ERROR)
 */
function RemoveUploadButton({ item, onRemove }) {
    if (item.uploadStatus === UPLOADING) return null;

    return (
        <button
            className="ai-preview-upload-remove-btn"
            onClick={(e) => {
                e.stopPropagation();
                onRemove?.(item);
            }}
        >
            <CloseIcon style={{ fontSize: 14 }} />
        </button>
    );
}

RemoveUploadButton.propTypes = {
    item: PropTypes.object.isRequired,
    onRemove: PropTypes.func
};

/**
 * Preview Image Item Component
 */
function AiPreviewImageItem({ image, index, onRemove, onPreview }) {
    const isPreviewable = image.uploadStatus === Constant.AI_CHAT_UPLOAD_STATUS.SUCCESS;
    const statusClassName = getUploadStatusClassName(image.uploadStatus);

    return (
        <div className={`ai-preview-upload-item ai-preview-upload-img-item ${statusClassName}`}>
            <img
                src={image.localUrl}
                alt={`upload-${index}`}
                className="ai-preview-upload-img"
                onClick={() => {
                    if (!isPreviewable) return;
                    onPreview?.(image);
                }}
            />
            <UploadStatusIndicator uploadStatus={image.uploadStatus} />
            <RemoveUploadButton item={image} onRemove={onRemove} />
        </div>
    );
}

AiPreviewImageItem.propTypes = {
    image: PropTypes.shape({
        id: PropTypes.string,
        localUrl: PropTypes.string,
        s3Path: PropTypes.string,
        uploadStatus: PropTypes.string
    }).isRequired,
    index: PropTypes.number,
    onRemove: PropTypes.func,
    onPreview: PropTypes.func
};

/**
 * Preview File Item Component - styled like reference image
 */
function AiPreviewFileItem({ file, onRemove }) {
    const statusClassName = getUploadStatusClassName(file.uploadStatus);

    return (
        <div className={`ai-preview-upload-item ai-preview-upload-file-item ${statusClassName}`}>
            <span className="ai-preview-file-icon">
                <AttachFileIcon extension={file?.fileExtension} background="#FFFFFF" />
            </span>
            <div className="ai-preview-file-info">
                <OverflowTooltipContent
                    text={file?.fileName}
                    className="ai-preview-file-name"
                    style={{ maxWidth: '200px' }}
                />
            </div>
            <UploadStatusIndicator uploadStatus={file.uploadStatus} />
            <RemoveUploadButton item={file} onRemove={onRemove} />
        </div>
    );
}

AiPreviewFileItem.propTypes = {
    file: PropTypes.shape({
        id: PropTypes.string,
        fileName: PropTypes.string,
        fileExtension: PropTypes.string,
        fileSize: PropTypes.number,
        s3Path: PropTypes.string,
        uploadStatus: PropTypes.string
    }).isRequired,
    onRemove: PropTypes.func
};

/**
 * AiPreviewUploadFiles - Combined preview for images and files
 */
function AiPreviewUploadFiles({
    uploadImgs = [],
    uploadFiles = [],
    onRemoveImage,
    onRemoveFile,
    onPreviewImage
}) {
    const hasImages = !Utils.isEmptyArray(uploadImgs);
    const hasFiles = !Utils.isEmptyArray(uploadFiles);

    if (!hasImages && !hasFiles) {
        return null;
    }

    return (
        <div className="ai-chat-input-preview-upload-files">
            {/* Preview Images */}
            {hasImages && uploadImgs.map((previewImg, index) => (
                <AiPreviewImageItem
                    key={previewImg.id}
                    image={previewImg}
                    index={index}
                    onRemove={onRemoveImage}
                    onPreview={onPreviewImage}
                />
            ))}

            {/* Preview Files */}
            {hasFiles && uploadFiles.map((previewFile) => (
                <AiPreviewFileItem
                    key={previewFile.id}
                    file={previewFile}
                    onRemove={onRemoveFile}
                />
            ))}
        </div>
    );
}

AiPreviewUploadFiles.propTypes = {
    uploadImgs: PropTypes.array,
    uploadFiles: PropTypes.array,
    onRemoveImage: PropTypes.func,
    onRemoveFile: PropTypes.func,
    onPreviewImage: PropTypes.func
};

export default AiPreviewUploadFiles;
